const DEFAULT_TIMEOUT = 600000;

type ConfigType = { timeout: number | null };

export class DataLock<T> {
  static defaultConfig: ConfigType = {
    timeout: DEFAULT_TIMEOUT,
  };

  private compareValue: T;
  requestTime?: number;
  timeout: number | null;

  /**
   * Compares given values to new.
   *
   * Allows for a strategic rate limiting.
   *
   * @param compareValue initial compare value
   * @param config - configuration object
   *
   * @class
   */
  constructor(compareValue: T, config: Partial<ConfigType> = {}) {
    const { timeout } = { ...DataLock.defaultConfig, ...config };
    this.timeout = timeout;

    this.compareValue = compareValue;
  }

  private hasTimeoutElapsed(newTime: number) {
    if (!this.timeout || !this.requestTime) {
      return false;
    }

    const timeElapsed = newTime - this.requestTime;
    return timeElapsed > this.timeout;
  }

  /**
   * Is the new value the same as the previous seen value?
   *
   * Compares using direct comparison
   */
  isSame(newValue: T) {
    if (this.hasTimeoutElapsed(Date.now())) {
      return false;
    }
    return newValue === this.compareValue;
  }

  /**
   * Compares new to stored and set's to stored to new if new !== different.
   *
   * Sets the value after comparison.
   *
   * @example ```js
   * const dl = new DataLock('myValue');
   * let v;
   *
   * if (dl.isSameOrSet('newValue') {
   *   v = 'myValue';
   * } else {
   *   v = 'newValue';
   * }
   *
   * assert(v === 'newValue')
   * >>> true
   * ```
   */
  isSameOrSet(newValue: T) {
    if (this.isSame(newValue)) {
      return true;
    }

    this.updateValue(newValue);
    return false;
  }

  updateValue(newValue: T) {
    this.compareValue = newValue;
    this.requestTime = Date.now();
  }
}
